import PostPromptModal from '@/components/bulletins/PostPromptModal';

import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock } from 'lucide-react';
import AnimatedTextarea from './AnimatedTextarea';
import BulletinImageUpload from './BulletinImageUpload';
import { useBulletinForm } from '@/hooks/use-bulletin-form';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import ClickPointsAnimation from '@/components/animations/ClickPointsAnimation';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { FocalPointSelector, FocalPoint } from '../forms/FocalPointSelector';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import UploadProgressModal from './UploadProgressModal';
import PostFlowOverlay from '@/components/posting/PostFlowOverlay';
import { useIsVerified, useVerificationStatus } from '@/hooks/use-verification';
import { VerificationGateModal } from '@/components/verification/VerificationGateModal';
import { VerificationForm } from '@/components/verification/VerificationForm';
import { VerificationCongratsModal } from '@/components/verification/VerificationCongratsModal';
import { supabase } from '@/integrations/supabase/client';

interface BulletinFormProps {
  onSuccess?: () => void;
  isUpdateTab?: boolean;
}

// Temporary hook for slow mode - in real implementation, this would come from a global store
const useSlowMode = () => {
  // This would come from the global context/store
  const [isSlowModeActive] = useState(true);
  return { isSlowModeActive };
};

const BulletinForm = ({ onSuccess, isUpdateTab = false }: BulletinFormProps) => {
  const { user, profile } = useAuth();
  const { isAdmin, isPremium } = useUserRoles();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showPointsAnimation, setShowPointsAnimation] = useState(false);
  const [postQueued, setPostQueued] = useState(false);
  const { isSlowModeActive } = useSlowMode();
  const {
    title,
    setTitle,
    content,
    setContent,
    selectedImage,
    isUploading,
    uploadProgress,
    uploadModal,
    handleImageSelect,
    handleSubmit,
    isArtstream,
    setIsArtstream,
    // Pull through disabled communities controls so modal toggles are respected
    disabledCommunities,
    setDisabledCommunities,
  } = useBulletinForm(isUpdateTab, () => {
    // This will only be called when submission is actually successful
    // Used for callbacks after successful post
  });

  const [showFocalPointSelector, setShowFocalPointSelector] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [focalPoint, setFocalPoint] = useState<FocalPoint>({ x: 50, y: 50 }); // Default center
  const [showPrompt, setShowPrompt] = useState(false);
  const [showPostOverlay, setShowPostOverlay] = useState(false);
  const [overlayMode, setOverlayMode] = useState<'full' | 'sheet'>('full');

  // Verification system states
  const { isVerified } = useIsVerified();
  const { request, hasPendingRequest, isRejected } = useVerificationStatus();
  const [showVerificationGate, setShowVerificationGate] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [hasShownCongrats, setHasShownCongrats] = useState(false);

  const checkVerifiedFresh = async (): Promise<boolean> => {
    if (!user?.id) return !!isVerified;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_verified_human')
        .eq('id', user.id)
        .maybeSingle();
      if (error) {
        console.warn('[BulletinForm] Fresh verify check error:', error);
        return !!isVerified;
      }
      const fresh = !!data?.is_verified_human;
      console.log('[BulletinForm] Fresh is_verified_human:', fresh);
      return fresh;
    } catch (e) {
      console.warn('[BulletinForm] Fresh verify check exception:', e);
      return !!isVerified;
    }
  };

  // Debug: Log profile state on mount and when it changes
  useEffect(() => {
    console.log('[BulletinForm] Profile state changed:', {
      profile_exists: !!profile,
      profile_username: profile?.username,
      profile_is_verified_human: profile?.is_verified_human,
      isVerified,
      user_id: user?.id
    });
  }, [profile, isVerified, user?.id]);

  // Check if user was just approved and show congrats modal
  useEffect(() => {
    if (isVerified && !hasShownCongrats && request?.status === 'approved') {
      setShowCongratsModal(true);
      setHasShownCongrats(true);
    }
  }, [isVerified, hasShownCongrats, request]);

  const openPrompt = async () => {
    if (!showPrompt) {
      // Check verification status before opening post modal
      console.log('[BulletinForm] Verification check:', {
        isVerified,
        profile_is_verified_human: profile?.is_verified_human,
        profile_exists: !!profile,
        profile_username: profile?.username
      });

      const freshVerified = await checkVerifiedFresh();
      if (!freshVerified) {
        console.log('[BulletinForm] User not verified, showing gate modal');
        setShowVerificationGate(true);
        return;
      }
      
      console.log('[BulletinForm] Opening PostPromptModal');
      setShowPrompt(true);
    }
  };

  const handleGetVerified = () => {
    setShowVerificationGate(false);
    setShowVerificationForm(true);
  };

  const handleVerificationFormSuccess = () => {
    // After submitting verification, show the pending state gate
    setShowVerificationGate(true);
  };
  
  // Debug log when the state changes
  React.useEffect(() => {
    console.log(`[BulletinForm] Modal state: ${showPrompt ? 'OPEN' : 'CLOSED'}`);
  }, [showPrompt]);

  // Modified image select handler to show focal point selector (for images only)
  const handleCustomImageSelect = (e: React.ChangeEvent<HTMLInputElement>, isMature: boolean = false) => {
    // First let the original handler process the file
    handleImageSelect(e, isMature);
    
    // Then prepare for focal point selection
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if the file is a video - skip focal point selection for videos
    const isVideo = file.type.startsWith('video/') || 
                  file.name.toLowerCase().endsWith('.mp4') || 
                  file.name.toLowerCase().endsWith('.webm') || 
                  file.name.toLowerCase().endsWith('.mov');
    
    if (isVideo) {
      // Skip focal point selection for videos
      console.log('Video upload detected - skipping focal point selection');
      return;
    }
    
    // Create URL for the focal point selector (images only)
    if (selectedFileUrl) {
      URL.revokeObjectURL(selectedFileUrl);
    }
    const fileUrl = URL.createObjectURL(file);
    setSelectedFileUrl(fileUrl);
    setFocalPoint({ x: 50, y: 50 }); // Reset to default
    setShowFocalPointSelector(true);
  };

  const handleFocalPointSelected = (newFocalPoint: FocalPoint) => {
    setFocalPoint(newFocalPoint);
    setShowFocalPointSelector(false);
  };

  const handleCancelFocalPoint = () => {
    setShowFocalPointSelector(false);
  };

  // Don't render the form in the updates tab if user is not an admin
  if (isUpdateTab && !isAdmin) {
    return null;
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    // Only show animation if the post was successful (not blocked by content moderation)
    e.preventDefault(); // Prevent form submission
    
    // Pass the focal point to the handleSubmit function
    const success = await handleSubmit(e, focalPoint);
    if (success) {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      if (buttonRect) {
        setShowPointsAnimation(true);
        // Show the post queued notification if slow mode is active
        if (isSlowModeActive) {
          setPostQueued(true);
          // Auto-hide after 5 seconds
          setTimeout(() => setPostQueued(false), 5000);
        }
      }
      setShowPrompt(false);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="mb-6">
      <div className="flex flex-col gap-4">
        <div className="flex gap-3 items-start">
          <Avatar className={cn(
            "h-8 w-8 mt-2",
            isAdmin && "ring-2 ring-[#F97316]",
            isPremium && "ring-2 ring-[#E5DEFF]",
            !isAdmin && !isPremium && "ring-1 ring-primary/20"
          )}>
            <AvatarImage src={profile?.profile_image_url} />
            <AvatarFallback>{profile?.username?.charAt(0) || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <AnimatedTextarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              isUpdateTab={isUpdateTab}
              onFocus={openPrompt}
              onKeyDown={openPrompt}
              onClick={openPrompt}
              onInput={openPrompt}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-4 ml-11">
          {/* Artstream toggle */}
          <div className="flex items-center gap-3 rounded-md border border-border/50 bg-[#171718] px-3 py-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Is it art?</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground underline cursor-help">What is this?</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">If enabled, this post will be featured in Artstream. Turn off for conversations or non-art posts.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch checked={isArtstream} onCheckedChange={setIsArtstream} />
          </div>
          <BulletinImageUpload
            selectedImage={selectedImage}
            onImageSelect={handleCustomImageSelect}
            uploadProgress={uploadProgress}
          />
          <Button 
            ref={buttonRef}
            type="submit" 
            className="ml-auto"
            disabled={isUploading}
          >
            {isUploading ? 'Posting...' : 'Post'}
          </Button>
        </div>
      </div>

      {/* Focal point selector modal */}
      {showFocalPointSelector && selectedFileUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <FocalPointSelector
            imageUrl={selectedFileUrl}
            onFocalPointSelected={handleFocalPointSelected}
            onCancel={handleCancelFocalPoint}
            initialFocalPoint={focalPoint}
          />
        </div>
      )}

      {/* Upload Progress Modal */}
      <UploadProgressModal
        isOpen={uploadModal.isOpen}
        state={uploadModal.state}
        onCancel={uploadModal.onCancel}
        onRetry={uploadModal.onRetry}
      />

      <ClickPointsAnimation 
        points={5}
        x={buttonRef.current?.getBoundingClientRect().right ?? 0}
        y={buttonRef.current?.getBoundingClientRect().top ?? 0}
        type="post"
        show={showPointsAnimation}
        onAnimationComplete={() => setShowPointsAnimation(false)}
      />
      
      {/* Post queued notification */}
      <AnimatePresence>
        {postQueued && (
          <motion.div 
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-1 px-2 py-1 mt-2 ml-11 bg-black/20 backdrop-blur-sm rounded-md text-xs w-fit"
          >
            <Clock className="h-3 w-3 text-gray-300" />
            <span className="text-gray-300">Your post is in the queue and will appear shortly</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fast prompt modal for Title / Description / Media */}
      <PostPromptModal
        open={showPrompt}
        title={title}
        description={content}
        onClose={() => setShowPrompt(false)}
        onSubmit={async (
          postCopy: string,
          mediaTitle: string,
          mediaDescription: string,
          image?: File | null,
          isArt?: boolean,
          disabledCommunityIds?: Set<string>,
          video?: File | null
        ) => {
          console.log('[BulletinForm] Modal submission received:', { 
            postCopy, 
            mediaTitle, 
            mediaDescription, 
            hasImage: !!image,
            hasVideo: !!video,
            isArt,
            disabledCommunityIdsSize: disabledCommunityIds?.size,
            disabledCommunityIdsArray: disabledCommunityIds ? Array.from(disabledCommunityIds) : []
          });
          
          // Update form state directly
          setTitle(mediaTitle);
          setContent(mediaDescription);

          // Apply modal-level toggles before submit so extraction respects them
          if (typeof isArt === 'boolean') {
            console.log('[BulletinForm] Setting isArtstream to:', isArt);
            setIsArtstream(isArt);
          }
          if (disabledCommunityIds && disabledCommunityIds.size >= 0) {
            console.log('[BulletinForm] Setting disabledCommunities to:', Array.from(disabledCommunityIds));
            setDisabledCommunities(disabledCommunityIds);
          }
          
          // If an image was provided through the modal, handle it (updates selectedImage state)
          if (image && image !== selectedImage) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(image);
            const mockEvent = {
              target: { files: dataTransfer.files },
              currentTarget: { files: dataTransfer.files }
            } as React.ChangeEvent<HTMLInputElement>;
            handleImageSelect(mockEvent);
          }
          
          // Open full-screen post flow overlay
          setOverlayMode('full');
          setShowPostOverlay(true);
          // Initialize stepper states
          window.dispatchEvent(new CustomEvent('post-flow:update', { detail: [
            { key: 'upload', state: 'pending', detail: '' },
            { key: 'moderation', state: 'pending', detail: '' },
            { key: 'save', state: 'pending', detail: '' },
            { key: 'crosspost', state: 'pending', detail: '' },
            { key: 'communities', state: 'pending', detail: '' },
            { key: 'constellations', state: 'pending', detail: '' },
          ]}));

          // Combine title and description for all posts (both text-only and media)
          let bulletinContent = postCopy || '';
          if (mediaDescription?.trim()) {
            bulletinContent = bulletinContent ? `${bulletinContent}\n\n${mediaDescription}` : mediaDescription;
          }
          
          console.log('[BulletinForm] About to call handleSubmit with disabledCommunityIds:', {
            size: disabledCommunityIds?.size,
            ids: disabledCommunityIds ? Array.from(disabledCommunityIds) : []
          });
          
          const success = await handleSubmit(
            null,
            focalPoint,
            bulletinContent,
            image ?? undefined,
            postCopy,
            disabledCommunityIds,
            video ?? undefined
          );
          if (success) {
            const buttonRect = buttonRef.current?.getBoundingClientRect();
            if (buttonRect) {
              setShowPointsAnimation(true);
              if (isSlowModeActive) {
                setPostQueued(true);
                setTimeout(() => setPostQueued(false), 5000);
              }
            }
            setShowPrompt(false);
          } else {
            console.error('[BulletinForm] Modal submission failed');
          }
        }}
        uploadProgress={uploadProgress}
      />

      {/* Post Flow Overlay */}
      <PostFlowOverlay
        open={showPostOverlay}
        mode={overlayMode}
        onMinimize={() => setOverlayMode('sheet')}
        onClose={() => setShowPostOverlay(false)}
      />

      {/* Verification Modals */}
      <VerificationGateModal
        open={showVerificationGate}
        onClose={() => setShowVerificationGate(false)}
        onGetVerified={handleGetVerified}
        isPending={hasPendingRequest}
        isRejected={isRejected}
      />

      <VerificationForm
        open={showVerificationForm}
        onClose={() => setShowVerificationForm(false)}
        onSuccess={handleVerificationFormSuccess}
      />

      <VerificationCongratsModal
        open={showCongratsModal}
        onClose={() => setShowCongratsModal(false)}
      />
    </form>
  );
};

export default BulletinForm;
